import { useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useEffect } from 'react';
import { fetchPolicy } from 'scenes/payments/redux/payment/thunks';
import { base64ToUtf8 } from 'scenes/common/helpers/text';
import useNextPage from './index';
import usePublishAnalyticsEvent, { AnalyticsEvent } from './useAnalytics';

type PolicyDetails = {
  policy: string;
  postCode: string;
};

/*
 Retrieves policy details from an encrypted policy details string
 E.g.
 encodedDetails = `cG9saWN5PUNPTEEtMDIyMC00MTczJnBvc3RDb2RlPTIxOTM=`
 return value = {
  policy: "COLA-0220-4173"
  postCode: "2193"
  }
 */
const getPolicyDetails = (
  encodedDetails: string
): PolicyDetails | undefined => {
  const unencoded = base64ToUtf8(encodedDetails);
  return unencoded
    ? Object.assign(
        {},
        ...unencoded
          .split('&')
          .map((item) => item.split('='))
          .map(([k, v]) => ({ [k]: v }))
      )
    : undefined;
};

const getHashedPolicyDetails = (searchParams: string): string => {
  const pattern = /hash=(?<hash>.*)/;
  const groups = searchParams.match(pattern)?.groups;
  return groups?.hash ?? '';
};

const useFetchPolicyWithEncodedQueryParams = () => {
  const { search } = useLocation();
  const nextPage = useNextPage();
  const dispatch = useDispatch();
  const analyticsErrorEvent = usePublishAnalyticsEvent(AnalyticsEvent.ERROR);
  useEffect(() => {
    if (search && search.length) {
      const policyDetails = getPolicyDetails(getHashedPolicyDetails(search));
      if (!policyDetails) {
        return;
      }
      const { policy, postCode } = policyDetails;
      const fetch = async () => {
        await dispatch(fetchPolicy(policy, postCode));
      };
      fetch()
        .then(() => nextPage())
        .catch((err) => {
          console.warn(err);
          analyticsErrorEvent();
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};

export default useFetchPolicyWithEncodedQueryParams;
